import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, FileSpreadsheet, Printer, Calendar, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { useStore } from '../store/useStore';

interface CalendarExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatDayLabel(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function formatDayTabLabel(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short'
  });
}


/** Lightens an ARGB color by mixing with white at given ratio (0–1 = 0%–100% white) */
function lightenArgb(hex: string, ratio = 0.25): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return 'FFFFFFFF';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const lr = Math.round(r + (255 - r) * (1 - ratio));
  const lg = Math.round(g + (255 - g) * (1 - ratio));
  const lb = Math.round(b + (255 - b) * (1 - ratio));
  return `FF${lr.toString(16).padStart(2, '0').toUpperCase()}${lg.toString(16).padStart(2, '0').toUpperCase()}${lb.toString(16).padStart(2, '0').toUpperCase()}`;
}

const EMOJI_MAP: Record<string, string> = {
  MapPin: '📍',
  MapPinIcon: '📍',
  Tent: '⛺',
  Music: '🎵',
  Utensils: '🍴',
  Coffee: '☕',
  Car: '🚗',
  Info: 'ℹ️',
  Ticket: '🎫',
  Star: '⭐',
  Shield: '🛡️',
  Store: '🏪',
  PlugConnected: '🔌',
  Drum: '🥁',
  Soup: '🥣',
  TruckElectric: '🚚',
  Passport: '🛂',
  Player: '🎮',
  Heart: '❤️',
  Camera: '📷',
  Wifi: '📶',
  Battery: '🔋',
  Flame: '🔥',
  Trash: '🗑️',
  Users: '👥',
  Cart: '🛒',
  Bell: '🔔',
  Magnifier: '🔍',
  MessageCircle: '💬',
  Wallet: '👛',
  CreditCard: '💳',
  CurrencyDollar: '💵',
  Accessibility: '♿',
  BrandBagsFm: '🛍️',
  Banana: '🍌',
  CameraOff: '🚫📷',
  InfoCircle: 'ℹ️',
  UsersGroup: '👥',
  UserPlus: '➕👤',
  UserCheck: '✔️',
  Logout: '🚪',
  LetterP: '🅿️',
  ShieldCheck: '🛡️✔️',
  Beer: '🍺',
  Wine: '🍷',
  Glasses: '👓',
  Radio: '📻',
  Tv: '📺',
  Key: '🔑',
  Hammer: '🔨',
  Bus: '🚌',
  AlertTriangle: '⚠️',
  Cloud1: '☁️',
  Cloud1Icon: '☁️',
};

function getIconEmoji(iconName?: string): string {
  if (!iconName) return '📍';
  return EMOJI_MAP[iconName] || '📍';
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function CalendarExportModal({ isOpen, onClose }: CalendarExportModalProps) {
  const { team, points, allPoints, shifts, categories, effectiveCategoryId } = useVisibleStore();
  const { currentUser, globalSettings, projects, currentProjectId } = useStore();

  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const isArtistManager = useMemo(() => {
    if (currentUser.role !== 'manager' || !currentUser.categoryId) return false;
    const cat = categories.find(c => c.id === currentUser.categoryId);
    return !!cat?.isArtistCategory;
  }, [currentUser, categories]);

  const currentProject = useMemo(() =>
    projects.find(p => p.id === currentProjectId), [projects, currentProjectId]
  );

  const projectName = currentProject?.name || 'Projet';
  const appLogo = globalSettings?.appLogoUrl;

  // ── Group shifts by day (only include category shifts if manager) ────────────
  const shiftsByDay = useMemo(() => {
    const map = new Map<string, typeof shifts>();
    const sourceShifts = effectiveCategoryId
      ? shifts.filter(sh => {
          const member = team.find(s => s.id === sh.teamId);
          return member?.categoryId === effectiveCategoryId;
        })
      : shifts;

    sourceShifts.forEach(sh => {
      const day = sh.start.split('T')[0];
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(sh);
    });
    // Sort shifts within each day by start time
    map.forEach((dayShifts, day) => {
      map.set(day, dayShifts.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()));
    });
    return map;
  }, [shifts, team, effectiveCategoryId]);

  const sortedDays = useMemo(() =>
    Array.from(shiftsByDay.keys()).sort(),
    [shiftsByDay]
  );

  // Points visible by this user (already filtered by useVisibleStore)
  const visiblePoints = allPoints || points;

  // Stage points for artist timetable
  const stagePoints = useMemo(() =>
    visiblePoints.filter(p => p.isStage),
    [visiblePoints]
  );

  // ── Helper to filter points shown for a given day ──────────────────────────
  const getPointsForDay = (dayShifts: typeof shifts, isStageOnly = false) => {
    const pts = isStageOnly ? stagePoints : visiblePoints;
    return pts.filter(pt => {
      const ptShifts = dayShifts.filter(s => s.pointId === pt.id);
      if (ptShifts.length === 0) return false;

      if (effectiveCategoryId) {
        const ptCat = categories.find(c => c.id === pt.categoryId);
        const isGeneral = !pt.categoryId || ptCat?.isGeneral;
        const belongsToMyCat = pt.categoryId === effectiveCategoryId;
        return belongsToMyCat || isGeneral;
      }
      return true;
    });
  };

  // ── Build a row data for a single shift ────────────────────────────────────
  const buildRow = (shift: typeof shifts[0]) => {
    const member = team.find(s => s.id === shift.teamId);
    const point = visiblePoints.find(p => p.id === shift.pointId);
    const cat = categories.find(c => c.id === member?.categoryId);
    return {
      heureDebut: formatTime(shift.start),
      heureFin: formatTime(shift.end),
      nom: member?.name || '—',
      emplacement: point?.name || '—',
      icone: getIconEmoji(point?.iconName),
      role: (member?.roles || (member?.role ? [member.role] : [])).join(', '),
      color: member?.color || '',
      categorie: cat?.name || '—',
    };
  };

  // ── Excel Export ────────────────────────────────────────────────────────────

  const handleExcelExport = async () => {
    setIsGeneratingExcel(true);
    try {
      const wb = XLSX.utils.book_new();

      // Header row style definitions
      const headerFill = { type: 'pattern' as const, patternType: 'solid' as const, fgColor: { argb: 'FF1E293B' } };
      const headerFont = { bold: true, color: { argb: 'FFFFFFFF' }, sz: 10 };

      for (const day of sortedDays) {
        const dayShifts = shiftsByDay.get(day) || [];
        const tabLabel = formatDayTabLabel(day);

        const ws = XLSX.utils.aoa_to_sheet([]);
        const sheetData: any[][] = [];
        const pointHeaderRowIndices = new Set<number>();

        // Row 0: Project title + Day
        sheetData.push([`${projectName} — ${formatDayLabel(day)}`, '', '', '', '', '']);
        // Row 1: empty spacer
        sheetData.push(['']);
        // Row 2: Headers
        sheetData.push(['Heure début', 'Heure fin', 'Nom & Prénom', 'Emplacement', 'Icône', 'Rôle']);

        // Write shifts grouped by point (only points with shifts for that day)
        const dayPoints = getPointsForDay(dayShifts);
        dayPoints.forEach(pt => {
          const ptShifts = dayShifts.filter(s => s.pointId === pt.id);

          // Point header row
          const headerRowIdx = sheetData.length;
          pointHeaderRowIndices.add(headerRowIdx);
          sheetData.push([`${getIconEmoji(pt.iconName)} ${pt.name}`, '', '', '', '', '']);

          ptShifts.forEach(shift => {
            const row = buildRow(shift);
            sheetData.push([row.heureDebut, row.heureFin, row.nom, row.emplacement, row.icone, row.role]);
          });
        });

        XLSX.utils.sheet_add_aoa(ws, sheetData, { origin: 'A1' });

        // Apply styles
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
        for (let R = range.s.r; R <= range.e.r; R++) {
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cellAddr = XLSX.utils.encode_cell({ r: R, c: C });
            if (!ws[cellAddr]) ws[cellAddr] = { v: '', t: 's' };
            if (!ws[cellAddr].s) ws[cellAddr].s = {};

            if (R === 0) {
              // Title row
              ws[cellAddr].s = { font: { bold: true, sz: 13 }, fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: 'FF0F172A' } }, font2: { color: { argb: 'FFFFFFFF' } } };
            } else if (R === 2) {
              // Header row
              ws[cellAddr].s = { font: headerFont, fill: headerFill };
            } else if (pointHeaderRowIndices.has(R)) {
              // Point header row
              ws[cellAddr].s = { font: { bold: true, sz: 10, italic: false }, fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: 'FF334155' } } };
            } else {
              // Data rows — detect team color for column C (Nom & Prénom)
              const cellValue = ws[cellAddr]?.v;
              if (C === 2 && cellValue && typeof cellValue === 'string') {
                const member = team.find(s => s.name === cellValue);
                if (member?.color) {
                  ws[cellAddr].s = { fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: lightenArgb(member.color, 0.6) } } };
                }
              }
            }
          }
        }

        // Column widths
        ws['!cols'] = [
          { wch: 13 }, // Heure début
          { wch: 13 }, // Heure fin
          { wch: 28 }, // Nom & Prénom
          { wch: 30 }, // Emplacement
          { wch: 10 }, // Icône
          { wch: 22 }, // Rôle
        ];

        // Merge title row
        if (!ws['!merges']) ws['!merges'] = [];
        ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } });

        XLSX.utils.book_append_sheet(wb, ws, tabLabel.slice(0, 31)); // Excel tab name max 31 chars
      }

      if (sortedDays.length === 0) {
        // Empty project — create a placeholder sheet
        const ws = XLSX.utils.aoa_to_sheet([['Aucun shift enregistré dans ce projet.']]);
        XLSX.utils.book_append_sheet(wb, ws, 'Aucun shift');
      }

      XLSX.writeFile(wb, `calendrier_${projectName.replace(/\s+/g, '_')}.xlsx`);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // ── PDF / Print Export ──────────────────────────────────────────────────────

  const generatePrintHtml = (): string => {
    const days = sortedDays;
    if (days.length === 0) return '<p>Aucun shift enregistré dans ce projet.</p>';

    const totalPages = days.length;

    const pages = days.map((day, pageIdx) => {
      const dayShifts = shiftsByDay.get(day) || [];
      const dayLabel = formatDayLabel(day);
      const pageNum = pageIdx + 1;

      let tableRows = '';

      if (isArtistManager && stagePoints.length > 0) {
        // ── Timetable mode ──────────────────────────────────────────────────
        const dayStagePoints = getPointsForDay(dayShifts, true);

        // Collect all time slots in 30-min increments covering all shifts this day
        const allTimes: number[] = [];
        dayShifts.forEach(sh => {
          const start = new Date(sh.start);
          const end = new Date(sh.end);
          for (let t = start.getTime(); t < end.getTime(); t += 30 * 60000) {
            allTimes.push(t);
          }
        });
        const uniqueTimes = Array.from(new Set(allTimes)).sort();

        const stageHeaders = dayStagePoints.map(pt => `<th style="background:#1e293b;color:#fff;padding:8px 12px;font-size:11px;font-weight:700;text-align:center;border:1px solid #e2e8f0;">${getIconEmoji(pt.iconName)}&nbsp;${pt.name}</th>`).join('');

        const timelineRows = uniqueTimes.map(t => {
          const slotStart = new Date(t);
          const timeLabel = slotStart.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

          const cells = dayStagePoints.map(pt => {
            const activeShift = dayShifts.find(sh =>
              sh.pointId === pt.id &&
              new Date(sh.start).getTime() <= t &&
              new Date(sh.end).getTime() > t
            );
            if (activeShift) {
              const member = team.find(s => s.id === activeShift.teamId);
              const bgColor = member?.color ? member.color + '40' : '#f0fdf4';
              const shiftTypeLabel = activeShift.shiftType === 'soundcheck' ? '🎤 Soundcheck' : activeShift.shiftType === 'performance' ? '🎵 Performance' : '';
              return `<td style="background:${bgColor};padding:6px 8px;font-size:10px;border:1px solid #e2e8f0;text-align:center;"><strong>${member?.name || '—'}</strong>${shiftTypeLabel ? `<br><span style="font-size:9px;color:#64748b;">${shiftTypeLabel}</span>` : ''}</td>`;
            }
            return `<td style="padding:6px 8px;border:1px solid #e2e8f0;background:#f8fafc;"></td>`;
          }).join('');

          return `<tr><td style="padding:6px 10px;font-size:10px;font-weight:600;color:#475569;border:1px solid #e2e8f0;white-space:nowrap;background:#f1f5f9;">${timeLabel}</td>${cells}</tr>`;
        }).join('');

        tableRows = `
          <table style="width:100%;border-collapse:collapse;font-family:system-ui,sans-serif;">
            <thead>
              <tr>
                <th style="background:#1e293b;color:#fff;padding:8px 12px;font-size:11px;font-weight:700;border:1px solid #e2e8f0;">Heure</th>
                ${stageHeaders}
              </tr>
            </thead>
            <tbody>${timelineRows || '<tr><td colspan="100%" style="text-align:center;padding:20px;color:#94a3b8;font-style:italic;">Aucun shift prévu</td></tr>'}</tbody>
          </table>`;

      } else {
        // ── Standard tabular mode ────────────────────────────────────────────
        const dayPoints = getPointsForDay(dayShifts);

        tableRows = dayPoints.map(pt => {
          const ptShifts = dayShifts.filter(s => s.pointId === pt.id);

          const pointHeader = `
            <tr>
              <td colspan="5" style="background:#1e293b;color:#fff;padding:8px 14px;font-size:11px;font-weight:700;border:1px solid #334155;">
                ${getIconEmoji(pt.iconName)}&nbsp;${pt.name}
              </td>
            </tr>`;

          const shiftRows = ptShifts.map((shift, idx) => {
            const row = buildRow(shift);
            const bg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
            const teamBg = row.color ? row.color + '28' : bg;
            return `
              <tr style="background:${bg};">
                <td style="padding:6px 12px;font-size:10px;border:1px solid #e8edf3;white-space:nowrap;font-weight:600;color:#1e293b;">${row.heureDebut}</td>
                <td style="padding:6px 12px;font-size:10px;border:1px solid #e8edf3;white-space:nowrap;font-weight:600;color:#475569;">${row.heureFin}</td>
                <td style="padding:6px 12px;font-size:10px;border:1px solid #e8edf3;font-weight:700;background:${teamBg};">${row.nom}</td>
                <td style="padding:6px 12px;font-size:10px;border:1px solid #e8edf3;color:#334155;">${row.emplacement}</td>
                <td style="padding:6px 12px;font-size:10px;border:1px solid #e8edf3;color:#64748b;">${row.role}</td>
              </tr>`;
          }).join('');

          return pointHeader + shiftRows;
        }).join('');

        tableRows = `
          <table style="width:100%;border-collapse:collapse;font-family:system-ui,sans-serif;">
            <thead>
              <tr style="background:#1e293b;color:#fff;">
                <th style="padding:9px 12px;font-size:10px;font-weight:700;text-align:left;border:1px solid #334155;white-space:nowrap;">Heure début</th>
                <th style="padding:9px 12px;font-size:10px;font-weight:700;text-align:left;border:1px solid #334155;white-space:nowrap;">Heure fin</th>
                <th style="padding:9px 12px;font-size:10px;font-weight:700;text-align:left;border:1px solid #334155;width:30%;">Nom &amp; Prénom</th>
                <th style="padding:9px 12px;font-size:10px;font-weight:700;text-align:left;border:1px solid #334155;width:30%;">Emplacement</th>
                <th style="padding:9px 12px;font-size:10px;font-weight:700;text-align:left;border:1px solid #334155;">Rôle</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>`;
      }

      const logoHtml = appLogo
        ? `<img src="${appLogo}" style="height:32px;width:auto;object-fit:contain;margin-right:12px;" />`
        : '';

      return `
        <div class="page" style="page-break-after:${pageIdx < totalPages - 1 ? 'always' : 'auto'};">
          <!-- Header -->
          <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 0 8px;border-bottom:2px solid #1e293b;margin-bottom:14px;">
            <div style="display:flex;align-items:center;">
              ${logoHtml}
              <div>
                <div style="font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-0.5px;">${projectName}</div>
                <div style="font-size:10px;color:#64748b;margin-top:1px;">${isArtistManager ? 'Time Table Artistes' : 'Planning des shifts'}</div>
              </div>
            </div>
            <div style="text-align:right;">
              <div style="font-size:13px;font-weight:700;color:#1e293b;">${dayLabel}</div>
              <div style="font-size:10px;color:#94a3b8;margin-top:2px;">Page ${pageNum} / ${totalPages}</div>
            </div>
          </div>

          <!-- Shift count badge -->
          <div style="margin-bottom:10px;display:flex;align-items:center;gap:8px;">
            <span style="background:#1e293b;color:#fff;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;">${dayShifts.length} shift${dayShifts.length !== 1 ? 's' : ''}</span>
            ${isArtistManager ? '<span style="font-size:10px;color:#64748b;font-style:italic;">Timetable — format festival</span>' : ''}
          </div>

          <!-- Table -->
          ${tableRows}

          <!-- Footer -->
          <div style="margin-top:14px;border-top:1px solid #e2e8f0;padding-top:6px;display:flex;justify-content:space-between;font-size:8px;color:#94a3b8;">
            <span>Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            <span>${projectName} — Usage interne</span>
          </div>
        </div>`;
    }).join('');

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Calendrier — ${projectName}</title>
  <style>
    @page { size: A4 landscape; margin: 12mm 14mm; }
    * { box-sizing: border-box; }
    body { font-family: system-ui, -apple-system, sans-serif; color: #0f172a; background: #fff; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>${pages}</body>
</html>`;
  };

  const handlePrintPreview = () => {
    setIsGeneratingPdf(true);
    try {
      const html = generatePrintHtml();
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        iframe.contentWindow?.focus();
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 2000);
        }, 600);
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[120]">
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-lg overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="p-5 border-b border-[#2D3139] flex justify-between items-center bg-[#1A1D23]">
          <h3 className="font-bold text-white flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-indigo-400" />
            </span>
            Export Calendrier
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-[#2D3139]">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto max-h-[70vh] custom-scrollbar">

          {/* Project Info */}
          <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/60 rounded-xl p-4">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Projet actif</p>
            <p className="text-white font-bold text-sm">{projectName}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="bg-indigo-500/10 text-indigo-400 text-[10px] font-bold px-2.5 py-1 rounded-full border border-indigo-500/20">
                {shifts.length} shift{shifts.length !== 1 ? 's' : ''} total
              </span>
              <span className="bg-slate-500/10 text-slate-400 text-[10px] font-bold px-2.5 py-1 rounded-full border border-slate-500/20">
                {sortedDays.length} jour{sortedDays.length !== 1 ? 's' : ''}
              </span>
              {isArtistManager && (
                <span className="bg-amber-500/10 text-amber-400 text-[10px] font-bold px-2.5 py-1 rounded-full border border-amber-500/20">
                  🎨 Mode Artiste
                </span>
              )}
            </div>
          </div>

          {/* Days preview */}
          {sortedDays.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Aperçu des jours</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                {sortedDays.map(day => {
                  const dayShifts = shiftsByDay.get(day) || [];
                  const isExpanded = expandedDay === day;
                  return (
                    <div key={day} className="border border-[#2D3139]/60 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedDay(isExpanded ? null : day)}
                        className="w-full flex items-center justify-between p-3 bg-[#1A1D23] hover:bg-[#2D3139]/40 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs font-bold text-white capitalize">{formatDayLabel(day)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400 font-medium">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</span>
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="p-3 space-y-1.5 bg-[#0A0B0E]/40">
                          {dayShifts.length === 0 ? (
                            <p className="text-[10px] text-slate-500 italic">Aucun shift</p>
                          ) : (
                            dayShifts.map(sh => {
                              const member = team.find(s => s.id === sh.teamId);
                              const point = visiblePoints.find(p => p.id === sh.pointId);
                              return (
                                <div key={sh.id} className="flex items-center gap-2 text-[10px]">
                                  <span className="text-slate-400 font-mono">{formatTime(sh.start)} – {formatTime(sh.end)}</span>
                                  <span
                                    className="font-bold truncate"
                                    style={{ color: member?.color || '#94a3b8' }}
                                  >
                                    {member?.name || '—'}
                                  </span>
                                  <span className="text-slate-500 truncate">@ {point?.name || '—'}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {sortedDays.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500 space-y-3">
              <Calendar className="w-12 h-12 opacity-20" />
              <p className="text-sm text-center">Aucun shift enregistré dans ce projet.<br />Commencez par créer des shifts dans le planning.</p>
            </div>
          )}

          {/* Format info */}
          <div className="grid grid-cols-2 gap-3 text-[10px] text-slate-400">
            <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/60 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel (.xlsx)
              </div>
              <p>Un onglet par jour • Triés par heure • Couleur team • Tous les points</p>
            </div>
            <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/60 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-blue-400 font-bold">
                <Printer className="w-3.5 h-3.5" />
                PDF A4 Paysage
              </div>
              <p>Une feuille par jour • Groupé par emplacement • Logo + en-tête</p>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-[#2D3139] bg-[#1A1D23] flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleExcelExport}
            disabled={isGeneratingExcel || sortedDays.length === 0}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all"
          >
            {isGeneratingExcel ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isGeneratingExcel ? 'Génération...' : 'Télécharger Excel'}
          </button>
          <button
            onClick={handlePrintPreview}
            disabled={isGeneratingPdf || sortedDays.length === 0}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all"
          >
            {isGeneratingPdf ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Printer className="w-4 h-4" />
            )}
            {isGeneratingPdf ? 'Génération...' : 'Aperçu Impression (PDF)'}
          </button>
        </div>

      </div>
    </div>
  );
}
